import { useEffect, useState } from "react";
import sliderImg from "../../assets/images/home/slider.png";
import home2 from "../../assets/images/home/home2.png";
import homeSlider2 from "../../assets/images/home/homeSlider2.png";
import directBidding from "../../assets/images/how-it-works/directBidding.png";
import connecting from "../../assets/images/how-it-works/connecting.png";
import negotiation from "../../assets/images/how-it-works/negotiation.jpg";
import payments from "../../assets/images/how-it-works/payments.jpg";
import homeSlider from "../../assets/images/home/homeSlider.png";
import transparency from "../../assets/images/how-it-works/transparency.jpg";
import marketplace from "../../assets/images/how-it-works/marketplace.jpg";

const dividerStyle = {
  borderBottom: "1px solid #e0e0e0",
  margin: "0 auto",
  maxWidth: "1200px",
};

const HowItWorksIcon = ({ src, alt }) => (
  <img src={src} alt={alt} className="w-12 h-12 object-contain rounded-xl bg-section-bg p-1 shadow mr-3 inline-block align-middle" />
);

export const Home = () => {
  // Carousel slides: each has image, headline, and description
  const slides = [
    {
      image: home2,
      headline: "Empowering Farmers",
      description: "Connecting growers and buyers for a thriving tomorrow."
    },
    {
      image: sliderImg,
      headline: "Smart Farming Solutions",
      description: "Leverage technology to boost productivity and sustainability."
    },
    {
      image: homeSlider2,
      headline: "Direct Market Access",
      description: "Eliminate middlemen and ensure fair prices for every harvest."
    },
    {
      image: homeSlider,
      headline: "Digital Marketplaces",
      description: "A transparent, tech-driven platform for seamless trade."
    },
    {
      image: transparency,
      headline: "Transparency & Trust",
      description: "Building trust with real-time insights and open communication."
    },
    {
      image: marketplace,
      headline: "Sustainable Growth",
      description: "Promoting eco-friendly practices for a greener future."
    },
  ];
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToSlide = (index) => setCurrentIndex(index);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [slides.length]);

  return (
    <div style={{ background: '#f8f5f0', minHeight: '100vh', width: '100%' }}>
      <main className="font-lato">
        {/* Hero Section */}
        <section
          className="relative flex flex-col md:flex-row items-center justify-between gap-8 py-10 md:py-20 px-2 md:px-8 bg-light-green/90 rounded-3xl shadow-xl w-full mx-auto mt-8 mb-12 overflow-hidden max-w-[1200px]"
          style={{ maxWidth: '70vw', marginLeft: 'auto', marginRight: 'auto' }}
        >
          {/* Carousel */}
          <div className="relative flex-1 w-full h-64 md:h-[420px] rounded-2xl overflow-hidden shadow-lg">
            {slides.map((slide, index) => (
              <div key={index} className={`absolute inset-0 w-full h-full transition-opacity duration-700 rounded-2xl ${index === currentIndex ? "opacity-100 z-10" : "opacity-0 z-0"}`} style={{ transition: 'opacity 0.7s' }}>
                <img
                  src={slide.image}
                  alt={slide.headline}
                  className="w-full h-full object-cover rounded-2xl"
                />
                {/* Overlay with gradient and text */}
                <div className="absolute inset-0 flex items-end md:items-center justify-center md:justify-start p-6 md:p-12" style={{ pointerEvents: 'none' }}>
                  <div className="w-full md:max-w-[60%] bg-gradient-to-t from-black/80 via-black/40 to-transparent rounded-2xl p-6 md:p-10" style={{ pointerEvents: 'auto' }}>
                    <h3 className="text-white text-2xl md:text-4xl font-extrabold mb-2 drop-shadow-lg" style={{ lineHeight: 1.15 }}>{slide.headline}</h3>
                    <p className="text-white text-sm md:text-base drop-shadow-md font-medium mb-2 md:mb-0" style={{ lineHeight: 1.4 }}>{slide.description}</p>
                  </div>
                </div>
              </div>
            ))}
            {/* Carousel Dots */}
            <div className="absolute bottom-4 left-0 w-full flex justify-center z-20">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`h-3 w-3 mx-1 rounded-full border-2 border-primary transition-all duration-200 ${index === currentIndex ? "bg-accent scale-125" : "bg-white opacity-60"}`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
          {/* Hero Text */}
          <div className="flex-1 flex flex-col justify-center items-start md:pl-8 mt-8 md:mt-0">
            <h1 className="text-primary text-2xl md:text-4xl lg:text-5xl font-extrabold mb-6 leading-tight">
              Halyavara: Empowering Indian Farmers, Connecting Growers and Buyers for a Thriving Tomorrow!
            </h1>
            <p className="bg-white/80 text-primary rounded-xl shadow p-6 mb-6 text-sm md:text-base leading-relaxed">
              At Halyavara India, our mission is to empower farmers across India by revolutionizing the agricultural supply chain. We are committed to providing a cutting-edge platform that connects farmers directly with food product companies, laborers, and farming equipment providers. Through our technology-driven ecosystem, we aim to eliminate middlemen, foster fair trade practices, and promote sustainable agriculture, ensuring that farmers receive fair prices for their produce and gain access to a broader market.
            </p>
            <a href="#features" className="inline-block bg-accent text-primary font-bold rounded-lg px-4 py-2 md:px-6 md:py-3 text-sm md:text-base shadow hover:bg-primary hover:text-white transition-colors duration-200">Learn More</a>
          </div>
        </section>
        <div style={dividerStyle}></div>

        {/* What We Offer / Solutions Section */}
        <section className="py-10 md:py-20 px-2 md:px-0 w-full">
          <div className="w-full max-w-[1200px] mx-auto">
            <h2 className="text-primary text-xl md:text-3xl md:text-4xl font-bold mb-8 text-center flex items-center justify-center gap-3">
              <span className="inline-block align-middle">🚀</span>
              What We Offer
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
              {/* Digital Marketplace */}
              <div className="card-modern flex flex-col items-center bg-white p-4 md:p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all w-full">
                <div className="mb-4 text-3xl md:text-4xl">🛒</div>
                <h3 className="text-primary text-lg md:text-xl font-bold mb-2 text-center">Digital Marketplace</h3>
                <p className="text-sm md:text-md leading-relaxed text-gray-800 text-center">
                  Connect directly with buyers and sellers across India. Our transparent, tech-driven marketplace ensures fair prices and real-time access to the best deals in agriculture.
                </p>
              </div>
              {/* Farm Intelligence */}
              <div className="card-modern flex flex-col items-center bg-white p-4 md:p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all w-full">
                <div className="mb-4 text-3xl md:text-4xl">📊</div>
                <h3 className="text-primary text-lg md:text-xl font-bold mb-2 text-center">Farm Intelligence</h3>
                <p className="text-sm md:text-md leading-relaxed text-gray-800 text-center">
                  Make smarter decisions with AI-powered insights on weather, soil health, and crop trends. We help you grow more with less risk and more confidence.
                </p>
              </div>
              {/* Agri-Finance */}
              <div className="card-modern flex flex-col items-center bg-white p-4 md:p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all w-full">
                <div className="mb-4 text-3xl md:text-4xl">💰</div>
                <h3 className="text-primary text-lg md:text-xl font-bold mb-2 text-center">Agri-Finance</h3>
                <p className="text-sm md:text-md leading-relaxed text-gray-800 text-center">
                  Access affordable loans, insurance, and financial tools designed for farmers. We empower you to invest in your future and protect your harvest.
                </p>
              </div>
              {/* Equipment & Services */}
              <div className="card-modern flex flex-col items-center bg-white p-4 md:p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all w-full">
                <div className="mb-4 text-3xl md:text-4xl">🚜</div>
                <h3 className="text-primary text-lg md:text-xl font-bold mb-2 text-center">Equipment & Services</h3>
                <p className="text-sm md:text-md leading-relaxed text-gray-800 text-center">
                  Rent or hire the latest farming equipment and skilled laborers with ease. Boost your productivity and efficiency with on-demand agri-services.
                </p>
              </div>
            </div>
          </div>
        </section>
        <div style={dividerStyle}></div>

        {/* Value Proposition Section */}
        <section className="py-10 md:py-20 px-2 md:px-0 w-full">
          <div className="w-full max-w-[1200px] mx-auto">
            <h2 className="text-primary text-3xl md:text-4xl font-bold mb-8 text-center flex items-center justify-center gap-3">
              <span className="inline-block align-middle">🌱</span>
              Our Unique Value
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="card-modern flex flex-col items-start bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all">
                <h3 className="text-primary text-xl font-bold mb-2 flex items-center gap-2"><span className="inline-block">🔗</span>Direct Transactions</h3>
                <p className="text-md leading-relaxed text-gray-800">
                  We facilitate direct transactions between farmers and food product companies, cutting out intermediaries and ensuring fair prices for farmers while enabling food companies to access fresh, traceable produce.
                </p>
              </div>
              <div className="card-modern flex flex-col items-start bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all">
                <h3 className="text-primary text-xl font-bold mb-2 flex items-center gap-2"><span className="inline-block">🛠️</span>Labor & Equipment Access</h3>
                <p className="text-md leading-relaxed text-gray-800">
                  Farmers can easily connect with skilled laborers and farming equipment providers, streamlining their farming operations and boosting productivity.
                </p>
              </div>
              <div className="card-modern flex flex-col items-start bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all">
                <h3 className="text-primary text-xl font-bold mb-2 flex items-center gap-2"><span className="inline-block">📈</span>Market Intelligence</h3>
                <p className="text-md leading-relaxed text-gray-800">
                  Our platform offers valuable market intelligence and insights, empowering farmers to make informed decisions based on demand trends, market prices, and consumer preferences.
                </p>
              </div>
              <div className="card-modern flex flex-col items-start bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all">
                <h3 className="text-primary text-xl font-bold mb-2 flex items-center gap-2"><span className="inline-block">🌿</span>Sustainability & Fair Trade</h3>
                <p className="text-md leading-relaxed text-gray-800">
                  We prioritize sustainable farming practices, ethical sourcing, and fair trade principles, aligning the interests of farmers and food product companies with responsible agriculture.
                </p>
              </div>
              <div className="card-modern flex flex-col items-start bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all">
                <h3 className="text-primary text-xl font-bold mb-2 flex items-center gap-2"><span className="inline-block">💡</span>Efficiency & Cost Savings</h3>
                <p className="text-md leading-relaxed text-gray-800">
                  By eliminating multiple steps in the supply chain, we optimize the procurement process, reduce administrative tasks, and save costs for farmers and food companies alike.
                </p>
              </div>
            </div>
          </div>
        </section>
        <div style={dividerStyle}></div>

        {/* How It Works Section */}
        <section className="py-10 md:py-20 px-2 md:px-0 w-full" id="features">
          <div className="w-full max-w-[1200px] mx-auto">
            <h2 className="text-primary text-3xl md:text-4xl font-bold mb-8 text-center flex items-center justify-center gap-3">
              <span className="inline-block align-middle">🔍</span>
              How It Works
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="card-modern flex flex-col items-start bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all">
                <h3 className="text-primary text-lg font-bold mb-2 flex items-center gap-2"><HowItWorksIcon src={connecting} alt="Sign Up" />Sign Up & Connect</h3>
                <p className="text-md leading-relaxed text-gray-800">
                  Farmers, buyers, and partners join the platform and create their profiles to start connecting.
                </p>
              </div>
              <div className="card-modern flex flex-col items-start bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all">
                <h3 className="text-primary text-lg font-bold mb-2 flex items-center gap-2"><HowItWorksIcon src={directBidding} alt="Direct Bidding" />Direct Bidding</h3>
                <p className="text-md leading-relaxed text-gray-800">
                  Farmers list their produce, and buyers place bids directly—ensuring transparency and fair pricing.
                </p>
              </div>
              <div className="card-modern flex flex-col items-start bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all">
                <h3 className="text-primary text-lg font-bold mb-2 flex items-center gap-2"><HowItWorksIcon src={negotiation} alt="Negotiation" />Negotiate & Finalize</h3>
                <p className="text-md leading-relaxed text-gray-800">
                  Both parties can negotiate terms, finalize deals, and schedule logistics—all within the platform.
                </p>
              </div>
              <div className="card-modern flex flex-col items-start bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all">
                <h3 className="text-primary text-lg font-bold mb-2 flex items-center gap-2"><HowItWorksIcon src={payments} alt="Payments" />Secure Payments</h3>
                <p className="text-md leading-relaxed text-gray-800">
                  Payments are processed securely, and farmers receive funds directly—no middlemen, no delays.
                </p>
              </div>
            </div>
          </div>
        </section>
        <div style={dividerStyle}></div>

        {/* Testimonial Section */}
        <section className="py-10 md:py-20 px-2 md:px-0 w-full">
          <div className="w-full max-w-[900px] mx-auto text-center">
            <h2 className="text-primary text-3xl md:text-4xl font-bold mb-8 flex items-center justify-center gap-3">
              <span className="inline-block align-middle">💬</span>
              What Our Farmers Say
            </h2>
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-8 bg-white p-4 md:p-10 rounded-2xl shadow-lg">
              <img src={home2} alt="Testimonial Farmer" className="w-24 h-24 md:w-32 md:h-32 object-cover rounded-full border-4 border-accent-green shadow mb-4 md:mb-0" />
              <blockquote className="text-base md:text-lg italic text-gray-800 font-semibold max-w-xl mx-auto">
                “Since joining Halyavara, I’ve been able to sell my crops at better prices and connect directly with buyers. The platform is easy to use and has truly changed my life!”
                <br />
                <span className="block mt-4 text-primary font-bold text-sm md:text-base">— Ramesh Kumar, Farmer</span>
              </blockquote>
            </div>
          </div>
        </section>
        <div style={dividerStyle}></div>

        {/* Partners/Trusted By Section */}
        {/*
        <section className="py-16 px-2 md:px-0 w-full">
          <div className="w-full max-w-[900px] mx-auto text-center">
            <h2 className="text-primary text-2xl md:text-3xl font-bold mb-8 flex items-center justify-center gap-3">
              <span className="inline-block align-middle">🤝</span>
              Trusted By
            </h2>
            <div className="flex flex-wrap justify-center items-center gap-10">
              <img src={logo} alt="Partner Logo" className="h-16 w-auto object-contain grayscale opacity-80 hover:opacity-100 transition-all" />
              <div className="h-16 w-32 bg-gray-200 rounded-lg flex items-center justify-center text-gray-400 text-xl font-bold">LOGO</div>
              <div className="h-16 w-32 bg-gray-200 rounded-lg flex items-center justify-center text-gray-400 text-xl font-bold">LOGO</div>
              <div className="h-16 w-32 bg-gray-200 rounded-lg flex items-center justify-center text-gray-400 text-xl font-bold">LOGO</div>
            </div>
          </div>
        </section>
        */}
      </main>
    </div>
  );
};
